import type { TabsProps as AntdTabsProps } from 'antd';
import cls from 'classnames';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export interface TabsProps {
  items: AntdTabsProps['items'];
  defaultKey: string | number;
  onChange?: (activeKey: string) => void;
  hiddenSplitLine?: boolean;
  activeLineWidth?: number;
  tabBarExtraContent?: React.ReactNode;
  styles?: Record<string, string>;
  selfStyles?: Record<string, string>;
}

export const TabsContext = createContext<{
  whenChange: (key: string) => void;
  setActiveKey: React.Dispatch<React.SetStateAction<string | number | undefined>>;
  activeKey: string | number | undefined;
}>({} as any);

const getTabIdByKey = (key: string | number) => `custom_tab_${key}`;

export function Tab({
  item,
  styles,
  selfStyles = {},
}: {
  item: { key: string; label: React.ReactNode };
  styles?: Record<string, string>;
  selfStyles?: Record<string, string>;
}) {
  const { whenChange, activeKey } = useContext(TabsContext);
  const onClick = useCallback(() => {
    whenChange(item.key);
  }, [item.key, whenChange]);

  return (
    <div
      onClick={onClick}
      className={cls(
        selfStyles.tab,
        {
          [selfStyles.active]: activeKey === item.key,
        },
        styles?.tab
      )}
      id={getTabIdByKey(item.key)}
    >
      {item.label}
    </div>
  );
}

export function UniTabs({
  items,
  onChange,
  defaultKey,
  hiddenSplitLine,
  activeLineWidth,
  tabBarExtraContent,
  styles,
  selfStyles = {},
}: TabsProps) {
  const [activeKey, setActiveKey] = useState(defaultKey || items?.[0]?.key);
  const whenChange = useCallback(
    (key: string) => {
      setActiveKey(key);
      onChange?.(key);
    },
    [onChange]
  );

  const [activePosition, setActivePosition] = useState<{ left: number; width: number } | null>(
    null
  );

  useEffect(() => {
    if (!activeKey) return;
    const id = getTabIdByKey(activeKey);
    const tabEle = document.querySelector<HTMLDivElement>(`#${id}`);
    if (!tabEle) return;
    const activeTabWidth = tabEle.clientWidth;
    const width = activeLineWidth ? Math.min(activeLineWidth, activeTabWidth) : activeTabWidth;
    const left = tabEle.offsetLeft + (activeTabWidth - width) / 2;
    setActivePosition({ left, width });
  }, [activeKey]);

  const activeChild = useMemo(
    () => items?.find((item) => item.key === activeKey)?.children || null,
    [activeKey, items]
  );

  return (
    <TabsContext.Provider value={{ whenChange, setActiveKey, activeKey }}>
      <div className={cls(selfStyles.tabsWrapper, styles?.tabs)}>
        <div className={selfStyles.tabs}>
          {items?.map((item) => (
            <Tab key={item.key} item={item} styles={styles} selfStyles={selfStyles} />
          ))}
        </div>
        {tabBarExtraContent}
        {activePosition && <div className={selfStyles.activeLine} style={{ ...activePosition }} />}
      </div>
      {!hiddenSplitLine && <div className={selfStyles.splitLine} />}
      {activeChild}
    </TabsContext.Provider>
  );
}
